/* Content for the "How to use Storefront" and "How to use Link in Bio" guides
   (FeatureGuideModal). Written for a first-time creator: short sentences, the
   same "Step X of 4" names the setup wizards show, one screenshot per step.
   The mobile guides (clanconnect_mobile influencer/components/zap/guide/
   guideContent.ts) carry the same text — keep the two in step. */

import sfName from '../../../../assets/images/zap-tour/storefront/01-name.jpeg';
import sfLook from '../../../../assets/images/zap-tour/storefront/02-look.jpeg';
import sfAbout from '../../../../assets/images/zap-tour/storefront/03-about.jpeg';
import sfDone from '../../../../assets/images/zap-tour/storefront/04-done.jpeg';
import bioName from '../../../../assets/images/zap-tour/link-in-bio/01-name.jpeg';
import bioLook from '../../../../assets/images/zap-tour/link-in-bio/02-look.jpeg';
import bioAbout from '../../../../assets/images/zap-tour/link-in-bio/03-about.jpeg';
import bioDone from '../../../../assets/images/zap-tour/link-in-bio/04-done.jpeg';

export interface GuideSection {
  id: string;
  /** Label on the section chip in the guide's sticky header. */
  nav: string;
  kicker?: string;
  title: string;
  body?: string;
  /** "How it works" rows: an icon and one line each. */
  flow?: { icon: string; text: string }[];
  shot?: { src: string; caption: string };
  /** Numbered steps. */
  steps?: string[];
  /** Option name + what it means. */
  choices?: [string, string][];
  tips?: { icon: string; text: string }[];
  success?: { title: string; text: string };
}

export interface FeatureGuide {
  name: string;
  lead: string;
  timeNote: string;
  sections: GuideSection[];
}

export const STOREFRONT_GUIDE: FeatureGuide = {
  name: 'Storefront',
  lead:
    'Your own shop page with the products you recommend. Share one link — people tap a product and buy it through your affiliate link.',
  timeNote: 'Setting it up takes about a minute.',
  sections: [
    {
      id: 'how',
      nav: 'How it works',
      title: 'How it works',
      flow: [
        { icon: '🏪', text: 'Create your shop in 4 quick steps.' },
        { icon: '🛍️', text: 'Add products with your affiliate links.' },
        { icon: '🔗', text: 'Share your shop link — in your Instagram bio, stories, anywhere.' },
      ],
    },
    {
      id: 'step1',
      nav: 'Step 1',
      kicker: 'Step 1 of 4 · Name',
      title: 'Name your shop',
      body: 'Type your shop name. We make your shop link from it automatically.',
      shot: { src: sfName, caption: 'Your shop name and link' },
      tips: [
        { icon: '🔄', text: 'See “Taken — try another”? Tap the refresh button next to the link for a new one, or edit the link yourself.' },
        { icon: '✏️', text: 'You can change the name any time later.' },
      ],
    },
    {
      id: 'step2',
      nav: 'Step 2',
      kicker: 'Step 2 of 4 · Look',
      title: 'Add your logo and cover',
      body: 'Pictures make the shop look like yours. The live preview below shows how it will look.',
      shot: { src: sfLook, caption: 'Logo and cover image' },
      choices: [
        ['Logo', 'A square picture works best — around 500 × 500.'],
        ['Cover image', 'A wide banner across the top — around 1600 × 500.'],
      ],
      tips: [{ icon: '⏭️', text: 'No picture handy? Tap Skip — you can add them later.' }],
    },
    {
      id: 'step3',
      nav: 'Step 3',
      kicker: 'Step 3 of 4 · About',
      title: 'Say what you sell',
      body: 'One line that shows under your shop name. We write one for you — keep it or change it.',
      shot: { src: sfAbout, caption: 'Your description' },
      tips: [{ icon: '🌐', text: 'Want to add your website or Instagram? Paste it in the optional link box.' }],
    },
    {
      id: 'step4',
      nav: 'Step 4',
      kicker: 'Step 4 of 4 · Done',
      title: 'Check it and open your shop',
      body: 'This is exactly what people will see. Pick the currency your prices show in, then tap Open my shop.',
      shot: { src: sfDone, caption: 'Your shop, ready to open' },
      tips: [{ icon: '🗂️', text: 'Anything you skipped shows “Add later” — you can add it in Store Settings.' }],
    },
    {
      id: 'after',
      nav: 'After',
      kicker: 'Your shop is open',
      title: 'Add products and share',
      steps: [
        'Go to the Products tab and tap Add Product.',
        'Paste the product’s link (for example from Amazon). The details fill in by themselves — check them.',
        'Add up to 5 pictures. The first one is the cover.',
        'Copy your Store URL and share it — put it in your Instagram bio.',
      ],
      choices: [
        ['Products', 'Add, edit and search the products in your shop.'],
        ['Analytics', 'See how many people visit and which products they click.'],
        ['Store Settings', 'Change your name, logo, cover or description any time.'],
      ],
      success: {
        title: '🎉 That’s it — your shop is live!',
        text: 'Tap View Store any time to see it the way shoppers do.',
      },
    },
  ],
};

export const LINK_IN_BIO_GUIDE: FeatureGuide = {
  name: 'Link in Bio',
  lead:
    'One page for your Instagram bio that holds all your links. Put one link in your bio — people tap it and see everything you share.',
  timeNote: 'Setting it up takes about a minute.',
  sections: [
    {
      id: 'how',
      nav: 'How it works',
      title: 'How it works',
      flow: [
        { icon: '📄', text: 'Create your page in 4 quick steps.' },
        { icon: '➕', text: 'Add your links — products, videos, anything.' },
        { icon: '📲', text: 'Put your page link in your Instagram bio.' },
      ],
    },
    {
      id: 'step1',
      nav: 'Step 1',
      kicker: 'Step 1 of 4 · Name',
      title: 'Name your page',
      body: 'Type the name people see at the top of your page. We make your page link from it automatically.',
      shot: { src: bioName, caption: '“This link is yours” means it’s free to use' },
      tips: [
        { icon: '🔄', text: 'Want a different link? Tap the refresh button next to it, or edit the link yourself.' },
        { icon: '✏️', text: 'You can change the name any time later.' },
      ],
    },
    {
      id: 'step2',
      nav: 'Step 2',
      kicker: 'Step 2 of 4 · Look',
      title: 'Add a photo and pick a colour',
      body: 'Your face and your colour make the page yours.',
      shot: { src: bioLook, caption: 'Photo, colour and button style' },
      choices: [
        ['Your photo', 'A square picture works best — around 500 × 500.'],
        ['Colour', 'Light, Dark, Sunset, Ocean or Grape.'],
        ['Button style', 'The shape of the buttons on your page.'],
      ],
      tips: [{ icon: '⏭️', text: 'Rather do it later? Tap Skip.' }],
    },
    {
      id: 'step3',
      nav: 'Step 3',
      kicker: 'Step 3 of 4 · About',
      title: 'Say who you are',
      body: 'One line under your name. We write one for you — keep it or change it.',
      shot: { src: bioAbout, caption: 'About you and your social links' },
      tips: [{ icon: '📸', text: 'Tap Add social link to show your Instagram, YouTube or anywhere else people can find you.' }],
    },
    {
      id: 'step4',
      nav: 'Step 4',
      kicker: 'Step 4 of 4 · Done',
      title: 'Check it and go live',
      body: 'This is exactly what people will see. Happy with it? Tap Go live.',
      shot: { src: bioDone, caption: 'Your page, ready to go live' },
      tips: [{ icon: '🗂️', text: 'Anything you skipped shows “Add later” — you can add it once your page is live.' }],
    },
    {
      id: 'after',
      nav: 'After',
      kicker: 'Your page is live',
      title: 'Add links and put it in your bio',
      steps: [
        'Go to the Links tab and tap Add block.',
        'Pick Link and paste the web page you want to share. Add as many as you like.',
        'Copy your Bio URL.',
        'In Instagram, tap Edit profile → Links → Add external link, and paste it.',
      ],
      choices: [
        ['Link', 'A button to any web page.'],
        ['Title', 'A heading to group your links.'],
        ['Text', 'A short note.'],
        ['Divider', 'Space between your links.'],
      ],
      tips: [
        { icon: '🎨', text: 'Appearance tab — change your photo, name, about line, social links and colour any time.' },
        { icon: '📊', text: 'Analytics tab — see how many people visit and which links they tap.' },
      ],
      success: {
        title: '🎉 That’s it — your page is live!',
        text: 'Tap View Bio any time to see it the way your followers do.',
      },
    },
  ],
};

/** The ⓘ help for step `stepIndex` (0-based) of a setup wizard, built from that
    step's guide section so the guide and the in-wizard help never disagree. */
export const stepHelpFromGuide = (guide: FeatureGuide, stepIndex: number) => {
  const s = guide.sections.find((x) => x.id === `step${stepIndex + 1}`);
  if (!s) return null;
  const [firstTip, ...moreTips] = s.tips || [];
  return {
    kicker: s.kicker,
    content: {
      title: s.title,
      image: s.shot?.src,
      points: [
        ...(s.body ? [s.body] : []),
        ...(s.choices || []).map(([name, desc]) => `${name} — ${desc}`),
        ...moreTips.map((t) => t.text),
      ],
      tip: firstTip?.text,
    },
  };
};
